import type { PortalConfig } from './config';
import {
  companyLogoUrl,
  fetchTerminalHeader,
  HeaderRequestFailed,
  retryDelayMs,
  type TerminalHeader,
} from './terminal-header';
import type { ConnectionState } from './types';

const STATE_LABELS: Record<ConnectionState, string> = {
  connecting: 'Connecting',
  connected: 'Live',
  reconnecting: 'Reconnecting',
  disconnected: 'Offline',
};

/** Shown until — or instead of — a resolved company. Never a blank bar, never an error. */
const FALLBACK_NAME = 'Attendance Portal';

/** How often a long-running kiosk re-checks its identity, so a rebrand eventually reaches the wall. */
const REFRESH_MS = 60 * 60 * 1000;

const CHECK_ICON = `
  <svg viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round">
    <path d="M20 6 9 17l-5-5"/>
  </svg>`;

/**
 * The board's top bar: company identity on the left, clock and connection state on the right.
 *
 * Renders in its fallback state immediately and never blocks on the network. The board behind it
 * connects and shows punches whether or not this ever resolves — a display that refused to show
 * attendance because it could not fetch a logo would be worse than one that says nothing about who
 * owns it.
 */
export class BoardHeader {
  readonly element: HTMLElement;

  private readonly mark: HTMLElement;
  private readonly name: HTMLElement;
  private readonly where: HTMLElement;
  private readonly address: HTMLElement;
  private readonly clock: HTMLElement;
  private readonly status: HTMLElement;
  private readonly statusLabel: HTMLElement;

  private clockTimer: number | undefined;
  private refreshTimer: number | undefined;

  constructor(private readonly config: PortalConfig) {
    this.element = document.createElement('header');
    this.element.className = 'topbar';
    this.element.innerHTML = `
      <div class="brand">
        <span class="brand-mark">${CHECK_ICON}</span>
        <span class="brand-text">
          <span class="brand-name"></span>
          <span class="brand-where" hidden></span>
          <span class="brand-address" hidden></span>
        </span>
      </div>
      <div class="topbar-right">
        <span class="bar-clock">—</span>
        <span class="status" data-state="connecting">
          <span class="status-dot"></span>
          <span class="status-label">Connecting</span>
        </span>
      </div>
    `;

    this.mark = this.element.querySelector('.brand-mark')!;
    this.name = this.element.querySelector('.brand-name')!;
    this.where = this.element.querySelector('.brand-where')!;
    this.address = this.element.querySelector('.brand-address')!;
    this.clock = this.element.querySelector('.bar-clock')!;
    this.status = this.element.querySelector('.status')!;
    this.statusLabel = this.element.querySelector('.status-label')!;

    this.name.textContent = FALLBACK_NAME;

    this.tickClock();
    this.clockTimer = window.setInterval(() => this.tickClock(), 1_000);
  }

  setConnectionState(state: ConnectionState): void {
    this.status.dataset.state = state;
    this.statusLabel.textContent = STATE_LABELS[state];
  }

  /**
   * Resolves the header for the first watched terminal, retrying indefinitely, then re-checking
   * hourly.
   *
   * Only the first code: a board can watch several terminals, but a header cannot honestly describe
   * two doors at once, and the company is the same for all of them anyway (a terminal's tenant owns
   * its codes).
   */
  start(): void {
    void this.resolve(this.config.codes[0]!);
  }

  /** Frees both timers. Present for symmetry and tests; a kiosk never tears the header down. */
  stop(): void {
    window.clearInterval(this.clockTimer);
    window.clearTimeout(this.refreshTimer);
  }

  private async resolve(code: string): Promise<void> {
    for (let attempt = 0; ; attempt++) {
      try {
        this.apply(await fetchTerminalHeader(this.config, code));

        // Resolved: stop retrying and fall back to the slow refresh cadence.
        this.refreshTimer = window.setTimeout(() => void this.resolve(code), REFRESH_MS);
        return;
      } catch (error) {
        const retryAfter = error instanceof HeaderRequestFailed ? error.retryAfterMs : null;
        await delay(retryDelayMs(attempt, retryAfter));
      }
    }
  }

  private apply(header: TerminalHeader | null): void {
    this.name.textContent = header?.companyName?.trim() || FALLBACK_NAME;

    // Branch and terminal answer the same question — "which door is this?" — so they share one line
    // and one separator, and either alone still reads correctly.
    const where = [header?.branchName, header?.terminalDescription]
      .map((part) => part?.trim())
      .filter(Boolean)
      .join(' · ');
    setLine(this.where, where);
    setLine(this.address, header?.companyAddress?.trim());

    this.renderLogo(header?.companyLogo ?? null);
  }

  private renderLogo(fileName: string | null): void {
    if (!fileName) {
      this.showFallbackMark();
      return;
    }

    const image = document.createElement('img');
    image.src = companyLogoUrl(this.config, fileName);
    image.alt = '';
    // A logo whose file has gone missing must not leave a broken frame on a wall — the generic mark
    // is a composed fallback, the browser's broken-image glyph reads as a fault.
    image.addEventListener('error', () => this.showFallbackMark());

    this.mark.dataset.logo = 'true';
    this.mark.replaceChildren(image);
  }

  private showFallbackMark(): void {
    delete this.mark.dataset.logo;
    this.mark.innerHTML = CHECK_ICON;
  }

  private tickClock(): void {
    const now = new Date();
    const hours = now.getHours();
    const hour12 = hours % 12 === 0 ? 12 : hours % 12;
    const minutes = String(now.getMinutes()).padStart(2, '0');
    this.clock.textContent = `${hour12}:${minutes} ${hours >= 12 ? 'PM' : 'AM'}`;
  }
}

function setLine(element: HTMLElement, value: string | undefined): void {
  element.textContent = value ?? '';
  element.hidden = !value;
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
